<script setup>
import { computed, ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: [Boolean, String],
        default: false
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    visible: {
        type: Boolean,
        default: true
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    returnYorN: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelValue', 'input', 'change']);

// 내부 로직
const ValidationMsg = ref('');
const isVisible = computed(() => props.visible);
const on = ref(false);

const normalizeToBool = (val) => {
    if (typeof val === 'boolean') return val;
    if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        if (['y', 'yes', 'true', '1'].includes(v)) return true;
        if (['n', 'no', 'false', '0', ''].includes(v)) return false;
    }
    return !!val;
};

const toInternal = (val) => (props.returnYorN ? String(val).trim().toUpperCase() === 'Y' : normalizeToBool(val));
const toExternal = (val) => (props.returnYorN ? (val ? 'Y' : 'N') : !!val);

const thumbX = computed(() => (on.value ? 'translate-x-0' : 'translate-x-full'));
const getBtnClass = (isActive, disabled) => [
    'relative z-10 h-full flex-1 flex items-center justify-center rounded-[0.35rem] text-[0.875rem]',
    'transition-colors duration-150 select-none',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    // 'mx-[2px]'  // ← 이건 지우세요. 슬라이드 박스 정렬 깨짐
    isActive ? 'text-[#111827] font-semibold' : 'text-gray-500 hover:font-bold',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0038ea]/40'
];

const selectValue = (value) => {
    if (props.disabled || props.readonly) return;
    if (on.value === value) return;

    on.value = value;
    const out = toExternal(on.value);
    emit('update:modelValue', out);
    emit('input', { id: props.inputId, value: out, checked: on.value });
    emit('change', out);
};

// Watchers
watch(
    () => props.modelValue,
    (newVal) => {
        on.value = toInternal(newVal);
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
    },
    { immediate: true, deep: true }
);
</script>

<template>
    <div v-if="isVisible" class="flex flex-col" :style="{ marginTop, marginBottom, marginLeft, marginRight }">
        <label v-if="props.label.toLowerCase() !== 'empty'" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ props.errmsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12 gap-0 p-[5px] border bg-[#f5f5f5] border-[#d4dbe4] rounded-[0.5rem] overflow-hidden" role="radiogroup" :aria-label="label">
            <div
                class="absolute top-[5px] left-[5px] bottom-[5px] w-[calc(50%-5px)] rounded-[0.35rem] bg-white border border-[#d4dbe4] shadow-sm transition-transform duration-300 ease-out pointer-events-none z-0"
                :class="thumbX"
                aria-hidden="true"
            ></div>
            <button type="button" :id="`${inputId}-on`" :class="getBtnClass(on === true, disabled || readonly)" :disabled="disabled || readonly" role="radio" :aria-checked="on === true" @click="selectValue(true)">ON</button>

            <button type="button" :id="`${inputId}-off`" :class="getBtnClass(on === false, disabled || readonly)" :disabled="disabled || readonly" role="radio" :aria-checked="on === false" @click="selectValue(false)">OFF</button>
        </div>
    </div>
</template>

<style scoped>
.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    white-space: nowrap;
    user-select: none;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}
</style>
